<template>
  <v-card v-if="canSign && sealItems.length" class="signing-card" flat>
    <div class="d-flex flex-row">
      <div class="d-flex flex-column w-100">
        <!-- TITLE -->
        <div class="d-flex flex-row text-body-medium pb-1 rounded-0">
          {{ i18n.t('titles.sealing_as') }}
        </div>
        <!-- /TITLE -->
        <!-- CARD MENU -->
        <DocumentMenu
          ref="documentMenu"
          :document="signStore.document"
          :policy="filteredPolicies"
          :sign-key="signKey"
        >
          <template #activator="{ props: ActivatorProps }">
            <v-btn
              v-bind="ActivatorProps"
              data-test-id="signing-card-options"
              icon
              style="position: absolute; right: 4px; top: 8px"
            >
              <VIcon class="text-grey-darken-1">
                far fa-ellipsis-vertical
              </VIcon>
            </v-btn>
          </template>
        </DocumentMenu>
        <!-- /CARD MENU -->
        <!-- /REQUEST DETAILS -->
      </div>
    </div>
    <!-- VISUAL SIGNATURE -->
    <div class="w-100 pb-4">
      <!-- REQUESTOR DETAILS -->
      <!-- REQUESTOR ORGANIZATION AND NAME -->
      <div
        class="d-flex flex-column flex-wrap text mb-4"
        data-test-id="requestor-org-name"
      >
        <!-- /REQUESTOR ORGANIZATION AND NAME -->
        <div
          class="d-flex align-center company-name text-truncate"
          :data-test-id="`organization-${initiatorCompanyDisplayName}`"
        >
          {{ initiatorCompanyDisplayName }}
        </div>
        <DocumentSealLevel v-if="selectedSeal" :seal="selectedSeal" />
      </div>

      <div
        v-if="selectedSeal"
        class="d-flex flex-column ga-4 justify-center align-center text-center"
      >
        <img
          v-if="previewSrc"
          alt="seal preview"
          class="seal-preview-image select-none pointer-events-none mt-auto"
          :src="previewSrc"
        />
        <div
          v-else
          class="seal-preview-placeholder select-none pointer-events-none bg-grey-lighten-2 h-100 w-100 mt-auto"
        ></div>
      </div>
      <div
        v-else
        class="seal-preview-placeholder d-flex bg-grey-lighten-2 h-100 w-100 mt-auto justify-center align-center"
      >
        <span>
          {{ i18n.t('texts.select_seal_placeholder') }}
        </span>
      </div>
    </div>
    <!-- /VISUAL SIGNATURE -->
    <v-combobox
      v-model="selectedSeal"
      autocomplete="off"
      class="mb-6 seal-combobox"
      :class="
        sealItems.length === 1 ? 'remove-icon pointer-events-none d-none' : ''
      "
      data-test-id="select-seal"
      hide-details="auto"
      :item-props="
        (item) => ({
          'data-test-id': `seal-${item.displayName.replace(/\s+/g, '-').toLowerCase()}`
        })
      "
      item-title="displayName"
      item-value="companyId"
      :items="sealItems"
      :placeholder="i18n.t('texts.select_seal')"
      :readonly="sealItems.length === 1"
      :rules="[() => !!selectedSeal || i18n.t('texts.seal_document_required')]"
      variant="outlined"
      @update:model-value="handleSealSelectionChange"
    />
    <!-- PLACE SIGNATURE -->
    <DeepButton
      v-if="!isSealPlaced"
      :action="onPlaceSeal"
      class="mt-2"
      :disabled="
        isSealPlaced ||
        !selectedSeal ||
        sealingState === 'sealing' ||
        !globalStore.pdfPreviewRendered ||
        !placeholderSetupComplete
      "
      :text="i18n.t('buttons.place_seal')"
      variant="outlined"
    />
    <!-- /PLACE SIGNATURE -->

    <div
      v-if="
        isSealPlaced &&
        signStore.document?.documentPolicy.canModifyAutographPosition
      "
      class="d-flex align-center"
      :class="
        isSealAnnotationPlaced ? 'btn-disabled-color pointer-events-none' : ''
      "
      style="height: 36px"
    >
      <MoveSignaturesMenu />
    </div>

    <v-text-field
      ref="annotationInput"
      v-model="sealAnnotationPlaceholder.text"
      autocomplete="off"
      class="annotation w-100 mt-6"
      :counter="sealAnnotationPlaceholder.text.length === 1000"
      data-test-id="annotation-text-field"
      density="comfortable"
      :disabled="
        !selectedSeal ||
        sealingState === 'sealing' ||
        !globalStore.pdfPreviewRendered ||
        !canAnnotate
      "
      :label="i18n.t('text.seal_annotation_layer')"
      maxlength="1000"
      variant="outlined"
      @update:model-value="handleAnnotationChange"
    />

    <!-- Seal -->
    <DeepButton
      :action="handleSeal"
      class="mt-2"
      data-test-id="sign-now"
      :disabled="!Boolean(selectedSeal) || !globalStore.pdfPreviewRendered"
      :loading="sealingState === 'sealing'"
      :text="i18n.t('buttons.seal_now')"
    />
    <!-- /Seal -->

    <!-- IDENTIFY DIALOG -->
    <IdentifyDialog />
    <DocumentDialog />
  </v-card>
  <v-alert
    v-if="documentStatus !== DocumentStatus.WITHDRAWN && !sealItems.length"
    class="mb-2"
    color="error"
    rounded
    variant="outlined"
  >
    <div class="d-flex flex-row align-center">
      <v-icon class="mr-2" color="error" size="14"
        >fas fa-triangle-exclamation</v-icon
      >
      <div class="d-flex flex-column align-center">
        {{ i18n.t('texts.no_available_seals') }}
      </div>
    </div>
  </v-alert>
  <ObserverCard
    v-if="!canSign && documentStatus === DocumentStatus.WITHDRAWN"
    :policies="filteredPolicies"
  />
</template>

<script setup lang="ts">
import ObserverCard from '@/components/cards/ObserverCard.vue';
import IdentifyDialog from '@/components/dialogs/IdentifyDialog.vue';
import DocumentMenu from '@/components/menus/DocumentMenu.vue';
import DocumentDialog from '@/components/dialogs/document-dialog/DocumentDialog.vue';
import DeepButton from '@/components/buttons/DeepButton.vue';
import DocumentSealLevel from '@/components/signatureArea/DocumentSealLevel.vue';
import { useGlobalStore } from '@/stores/global.ts';
import { useSignStore } from '@/stores/sign.ts';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getSealPreview } from '@/api/deepsign/users-me-seals-preview.ts';
import { DocumentStatus } from '@/types/enums/DocumentStatus.ts';
import { useSignContext } from '@/composables/useSignContext.ts';
import { constants } from '@/constants/constants.ts';
import {
  defaultAnnotationPosition,
  defaultAnnotationResizeDimensions
} from '@/composables/placement/usePlacement.ts';
import type { SealAnnotation } from '@/types/deepsign/SealAnnotation.ts';
import {
  injectStrict,
  PlaceHolderComposableKey,
  PlaceHolderSealComposableKey
} from '@/composables/placement/placementSymbols.ts';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import { createPlaceholderPayload } from '@/composables/placement/usePlacementUtility.ts';
import { useDisplay } from 'vuetify';
import MoveSignaturesMenu from '@/components/menus/MoveSignaturesMenu.vue';

const props = defineProps({
  document: {
    type: Object,
    default: undefined
  },
  signatures: {
    type: Array,
    default: () => []
  },
  canSign: {
    type: Boolean,
    default: false
  }
});
const display = useDisplay();
const signStore = useSignStore();
const i18n = useI18n();
const globalStore = useGlobalStore();
const { policy, signKey } = useSignContext();
const selectedSeal = ref(globalStore.selectedSeal || null);
const sealingState = ref<'idle' | 'sealing' | 'error'>('idle');
const sealItems = ref([]);
const annotationInput = ref();
const previewSrc = ref('');

const documentStatus = computed(() => signStore.document?.documentStatus);
const initiatorCompanyDisplayName = computed(() => {
  return props.document?.initiatorCompanyDisplayName || '';
});

const {
  resetPlaceholderState,
  placeholderTemplateRefs,
  placeholderSetupComplete
} = injectStrict(PlaceHolderComposableKey);

const {
  sealAnnotationPlaceholder,
  sealPlaceholder,
  handleInitialSealAnnotationPosition,
  handleInitialSealPosition
} = injectStrict(PlaceHolderSealComposableKey);

const isSealPlaced = computed(() => {
  return sealPlaceholder.value?.isPlaced || false;
});

const isSealAnnotationPlaced = computed(() => {
  return sealAnnotationPlaceholder.value?.isPlaced || false;
});

const filteredPolicies = computed(() =>
  policy.value
    ? Object.keys(policy.value)
        .filter((key) => !constants.SEAL_POLICIES_TO_OMIT.includes(key))
        .reduce((acc, key) => {
          acc[key] = policy.value[key];
          return acc;
        }, {})
    : {}
);

const canAnnotate = computed(() => {
  return signStore.document?.documentPolicy.canAnnotate || false;
});

onUnmounted(() => {
  globalStore.selectedSeal = null;
});

async function handleSeal() {
  // Call the API to seal
  try {
    sealingState.value = 'sealing';
    if (globalStore.selectedSeal) {
      let annotationPayload: SealAnnotation | null;
      if (
        (sealAnnotationPlaceholder.value &&
          !sealAnnotationPlaceholder.value.text) ||
        !canAnnotate.value
      ) {
        resetPlaceholderState(
          sealAnnotationPlaceholder.value as Placeholder,
          defaultAnnotationPosition,
          defaultAnnotationResizeDimensions
        );
        annotationPayload = null;
      } else {
        const annotationPosition = await createPlaceholderPayload(
          sealAnnotationPlaceholder?.value,
          placeholderTemplateRefs?.value
        );
        annotationPayload = {
          text: sealAnnotationPlaceholder?.value?.text,
          position: annotationPosition
        } as SealAnnotation;
      }

      const sealPosition = signStore.document?.documentPolicy
        .canModifyAutographPosition
        ? await createPlaceholderPayload(
            sealPlaceholder?.value,
            placeholderTemplateRefs?.value
          )
        : null;

      const payload = {
        sign: {
          autographPosition: sealPosition
        },
        annotation: annotationPayload,
        sealId: globalStore.selectedSeal.sealId
      };
      await signStore.sealDocument(signStore.document!.documentId, payload);
    }
    sealingState.value = 'idle';
  } catch (error) {
    sealingState.value = 'error';
  }
}

async function getSealImage(sealId: string) {
  try {
    const IMAGE_SIZE_FACTOR = 3;
    const result = await getSealPreview(sealId, IMAGE_SIZE_FACTOR);
    previewSrc.value = URL.createObjectURL(result.data);
    signStore.sealPreviewUrl = previewSrc.value;
  } catch (e) {
    console.error('Get Seal Preview failed: ', e);
    return e;
  }
}

function handleAnnotationChange(inputValue: string) {
  if (!inputValue) return;
  if (
    inputValue.length > 0 &&
    sealAnnotationPlaceholder.value &&
    !sealAnnotationPlaceholder.value.isPlaced &&
    !sealAnnotationPlaceholder.value.isVisible
  ) {
    sealAnnotationPlaceholder.value.isPlaced = true;
    sealAnnotationPlaceholder.value.isDraggable = true;
    handleInitialSealAnnotationPosition();
  }
}

function handleSealSelectionChange(value: object) {
  if (
    value &&
    value?.sealId !== globalStore?.selectedSeal?.sealId &&
    sealAnnotationPlaceholder.value
  ) {
    sealAnnotationPlaceholder.value.text = '';
  }
}

function onPlaceSeal() {
  handleInitialSealPosition();
  if (display.mdAndDown.value) {
    globalStore.drawer = false;
  }
}

watch(
  () => selectedSeal,
  async (newValue) => {
    if (!newValue || !newValue.value) return;
    globalStore.setSelectedSeal(newValue.value);
    if (newValue.value) {
      await getSealImage(newValue.value.sealId);
    }
  },
  {
    deep: true,
    immediate: true
  }
);

watch(
  () => signStore.document?.signContext.availableSeals,
  (newValue) => {
    if (newValue && Array.isArray(newValue) && newValue.length > 0) {
      try {
        sealItems.value = newValue.filter(
          (item) =>
            item.company.displayName ===
            props.document?.initiatorCompanyDisplayName
        );

        if (sealItems.value?.length === 1) {
          selectedSeal.value = sealItems.value[0];
        }
      } catch (e) {
        console.error('Setup Seals for company failed: ', e);
        sealItems.value = [];
      }
    }
  },
  {
    deep: true,
    immediate: true
  }
);
</script>

<style scoped lang="scss">
.signing-card {
  box-sizing: border-box;
  border-radius: 7px;
  background-color: rgb(var(--v-theme-grey-lighten-5));
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgb(var(--v-theme-grey-lighten-2));
  margin-bottom: 32px;
  margin-top: 16px;
  padding: 1rem;
}

.signee-email {
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.0025em;
  color: black !important;
  padding-bottom: 8px;

  &.mobile {
    font-size: 16px;
  }
}

.seal-preview-image {
  padding: 1px;
}

.seal-preview-placeholder {
  padding: 1px;
  min-height: 193px;
}

.signature-level {
  color: rgb(var(--v-theme-grey));
  font-weight: 400 !important;
  margin-right: 6px;
  font-size: 14px !important;
}

.actions-container {
  width: 100%;
}

.signed {
  margin-right: 4px;
  color: rgb(var(--v-theme-success));
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
}

.signed-icon {
  margin-top: -28px;
  color: rgb(var(--v-theme-success));
}

.date {
  color: rgb(var(--v-theme-grey));
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
}

.default-autograph {
  margin-top: 16px;
  margin-bottom: 10px;
  width: 100%;
  aspect-ratio: 140/47;
  border: 1px solid rgb(var(--v-theme-grey));
  border-radius: 4px;
}

.change-default-autograph {
  width: 100%;
  padding-left: 0;
  padding-right: 0;
}

.change-button {
  &:hover {
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
  }

  &:hover :deep(.v-icon.v-icon) {
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
  }
}

.menu-icon {
  color: rgb(var(--v-theme-grey));
}

.tooltip {
  box-shadow: 0 3px 10px rgb(0 0 0 / 0.2) !important;
}

.deep-caption-grey {
  color: rgb(var(--v-theme-grey-lighten-1));
}

.too-large {
  color: rgb(var(--v-theme-red-accent-2));
}

.authority-service {
  color: rgb(var(--v-theme-lighten-1));
  margin-top: 8px;
  width: 100%;
}

.move-signatures-menu {
  padding-top: 1rem;
}

.remove-icon :deep(.v-field__append-inner .v-combobox__menu-icon) {
  display: none !important;
}

.company-name {
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.0025em;
  color: black !important;

  &.mobile {
    font-size: 16px;
  }
}

.seal-combobox :deep(.v-input__details) {
  padding-inline: 0;
}

:deep(.seal-combobox input::placeholder) {
  color: black !important;
  opacity: 1;
}
.annotation :deep(.v-field__input) {
  height: 100%;
}
.btn-disabled-color > * {
  color: rgb(var(--v-theme-grey));
}
</style>
